import { useEffect, useMemo, useState } from "react";

import { getDashboard } from "./api";
import { CurrentPricesSection } from "./CurrentPricesSection";
import { FAQSection } from "./FAQSection";
import { TrendChart } from "./TrendChart";
import type { DashboardPayload, FuelType, PredictionRecord, ProductData } from "./types";

function LogoMark() {
  return (
    <span className="logo-mark" aria-hidden="true">
      <svg viewBox="0 0 32 32">
        <path d="M8 4h12a3 3 0 0 1 3 3v20H8V4Z" fill="none" stroke="currentColor" strokeWidth="2.4" />
        <path d="M12 8h7v6h-7zM23 10l4 4v8a2 2 0 0 1-4 0M11 27v-5h9v5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function ArrowIcon({ direction }: { direction: PredictionRecord["direction"] }) {
  const rotation = direction === "decrease" ? 135 : direction === "increase" ? -45 : 0;
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" style={{ transform: `rotate(${rotation}deg)` }}>
      <path d="M5 12h14M14 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function formatDate(value: string | null): string {
  if (!value) return "Awaiting data";
  return new Intl.DateTimeFormat("en-ZA", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Africa/Johannesburg",
  }).format(new Date(value.length === 10 ? `${value}T12:00:00Z` : value));
}

function formatRand(cents: number): string {
  return `${cents < 0 ? "−" : cents > 0 ? "+" : ""}R${(Math.abs(cents) / 100).toFixed(2)}`;
}

function recoveryLabel(cents: number): string {
  if (Math.abs(cents) < 0.5) return "Balanced";
  return cents > 0 ? "Over-recovery" : "Under-recovery";
}

function ProductTabs({
  products,
  active,
  onChange,
}: {
  products: ProductData[];
  active: FuelType;
  onChange: (fuel: FuelType) => void;
}) {
  return (
    <div className="product-tabs" role="tablist" aria-label="Fuel product">
      {products.map((product) => (
        <button
          key={product.fuelType}
          role="tab"
          aria-selected={product.fuelType === active}
          className={product.fuelType === active ? "active" : ""}
          onClick={() => onChange(product.fuelType)}
        >
          {product.label}
        </button>
      ))}
    </div>
  );
}

function EmptyPanel() {
  return (
    <section className="empty-panel">
      <span className="eyebrow">Pipeline ready</span>
      <h2>Waiting for the first CEF ingestion run</h2>
      <p>Once the scheduled Lambda stores a report, recovery history and predictions will appear here automatically.</p>
    </section>
  );
}

export default function App() {
  const [dashboard, setDashboard] = useState<DashboardPayload | null>(null);
  const [activeFuel, setActiveFuel] = useState<FuelType>("petrol_95");
  const [isDemo, setIsDemo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getDashboard(controller.signal)
      .then(({ data, isDemo: demo }) => {
        setDashboard(data);
        setIsDemo(demo);
      })
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        setError("Live recovery data is temporarily unavailable. Please try again shortly.");
      });
    return () => controller.abort();
  }, []);

  const activeProduct = useMemo(
    () => dashboard?.products.find((product) => product.fuelType === activeFuel) ?? null,
    [activeFuel, dashboard],
  );

  const prediction = activeProduct?.prediction ?? null;
  const latest = activeProduct?.latest ?? null;
  const cycleProgress = prediction
    ? Math.min(100, Math.round((prediction.observedDays / prediction.expectedDays) * 100))
    : 0;

  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="Fuel Price Tracker South Africa home">
          <LogoMark />
          <span><strong>Fuel Price Tracker</strong><small>South Africa</small></span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#current-prices">Current prices</a>
          <a href="#forecast">Forecast</a>
          <a href="#trend">Recovery trend</a>
          <a href="#faq">FAQ</a>
          <a href="#methodology">How it works</a>
          <a className="source-link" href={dashboard?.source.url ?? "https://cefgroup.co.za/daily-basic-fuel-price/"} target="_blank" rel="noreferrer">
            CEF source <span aria-hidden="true">↗</span>
          </a>
        </nav>
      </header>

      <main id="top">
        {isDemo ? (
          <div className="demo-banner" role="status">
            Preview mode: these are labelled sample values. Connect <code>VITE_API_BASE_URL</code> for live CEF data.
          </div>
        ) : null}
        {error ? <div className="error-banner" role="alert">{error}</div> : null}

        <section className="hero" id="forecast">
          <div className="hero-copy">
            <span className="eyebrow"><span className="live-dot" /> Daily CEF recovery monitor</span>
            <h1>Know the likely fuel price move <em>before</em> it happens.</h1>
            <p>We translate South Africa’s daily fuel recovery figures into a clear, confidence-rated estimate for the next official adjustment.</p>
            <div className="hero-meta">
              <span>Next expected adjustment</span>
              <strong>{formatDate(dashboard?.targetDate ?? null)}</strong>
              <span className="meta-divider" />
              <span>Updated</span>
              <strong>{formatDate(dashboard?.updatedAt ?? null)}</strong>
            </div>
          </div>

          <aside className="forecast-card" aria-label="Current forecast">
            <div className="card-topline">
              <span>{activeProduct?.label ?? "Petrol 95"}</span>
              {prediction ? <span className={`confidence ${prediction.confidence}`}>{prediction.confidence} confidence</span> : null}
            </div>
            {prediction ? (
              <>
                <div className={`direction-icon ${prediction.direction}`}><ArrowIcon direction={prediction.direction} /></div>
                <p className="forecast-kicker">Likely price {prediction.direction}</p>
                <div className="forecast-value">{formatRand(prediction.predictedChangeCents)}<small>/ litre</small></div>
                <p className="forecast-range">Expected range: <strong>{formatRand(prediction.rangeLowCents)} to {formatRand(prediction.rangeHighCents)}</strong></p>
                <div className="progress-label"><span>Pricing cycle</span><strong>{prediction.observedDays} of {prediction.expectedDays} days</strong></div>
                <div className="progress-track"><span style={{ width: `${cycleProgress}%` }} /></div>
              </>
            ) : (
              <p className="card-loading">Calculating after the first complete ingestion run…</p>
            )}
          </aside>
        </section>

        <CurrentPricesSection />

        {dashboard ? (
          <section className="dashboard-section">
            <ProductTabs products={dashboard.products} active={activeFuel} onChange={setActiveFuel} />
            {activeProduct && latest ? (
              <>
                <div className="metric-grid">
                  <article className="metric-card primary">
                    <span>Cycle average recovery</span>
                    <strong>{latest.averageRecoveryCents > 0 ? "+" : ""}{latest.averageRecoveryCents.toFixed(1)}<small> c/l</small></strong>
                    <p className={latest.averageRecoveryCents >= 0 ? "positive" : "negative"}>{recoveryLabel(latest.averageRecoveryCents)}</p>
                  </article>
                  <article className="metric-card">
                    <span>Latest daily movement</span>
                    <strong>{latest.dailyRecoveryCents > 0 ? "+" : ""}{latest.dailyRecoveryCents.toFixed(1)}<small> c/l</small></strong>
                    <p>CEF report · {formatDate(latest.recordDate)}</p>
                  </article>
                  <article className="metric-card">
                    <span>Forecast confidence</span>
                    <strong>{prediction?.confidenceScore ?? 0}<small>%</small></strong>
                    <p>Rises as the cycle fills</p>
                  </article>
                </div>

                <article className="trend-panel" id="trend">
                  <div className="panel-heading">
                    <div><span className="eyebrow">Cycle-to-date</span><h2>{activeProduct.label} recovery trend</h2></div>
                    <div className="chart-legend"><span /><span>Over-recovery supports a decrease</span></div>
                  </div>
                  <TrendChart points={activeProduct.history} label={activeProduct.label} />
                  <p className="chart-note">Positive recovery values generally point to a price decrease; negative values point to an increase. Statutory levy changes are not predicted.</p>
                </article>
              </>
            ) : <EmptyPanel />}
          </section>
        ) : (
          <div className="loading-state"><span /> Loading the latest recovery cycle…</div>
        )}

        <FAQSection />

        <section className="method-section" id="methodology">
          <div className="method-intro">
            <span className="eyebrow">Transparent by design</span>
            <h2>From a technical PDF to one useful answer.</h2>
            <p>The forecast is deliberately simple and explainable. Every displayed observation links back to the public source that produced it.</p>
          </div>
          <div className="method-steps">
            <article><span>01</span><h3>Collect</h3><p>A scheduled Lambda finds and validates the newest CEF daily report.</p></article>
            <article><span>02</span><h3>Project</h3><p>Recent daily recovery is weighted, extrapolated, and bounded by observed volatility.</p></article>
            <article><span>03</span><h3>Explain</h3><p>The dashboard shows a range and confidence score—never false precision.</p></article>
          </div>
        </section>
      </main>

      <footer>
        <div className="brand footer-brand"><LogoMark /><span><strong>Fuel Price Tracker</strong><small>South Africa</small></span></div>
        <div className="footer-center">
          <p>{dashboard?.disclaimer ?? "Independent estimate based on public CEF data."}</p>
          <p className="powered-by">
            Powered by{" "}
            <a href="https://radebeinnovations.co.za/" target="_blank" rel="noreferrer">
              Radebe Innovations
            </a>
            {" & "}
            <a href="https://www.paletech.co.za/" target="_blank" rel="noreferrer">
              PaleTech
            </a>
          </p>
        </div>
        <a href={dashboard?.source.url ?? "https://cefgroup.co.za/daily-basic-fuel-price/"} target="_blank" rel="noreferrer">View official CEF data ↗</a>
      </footer>
    </div>
  );
}
